import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { PRODUCTS } from '../../data/products.mjs';
import { formatPrice } from '../../utils/currency.mjs';
import './softwareshowcase.css';

const isInternal = (href) => href && href.startsWith('/');

/** Interfaz de ejemplo dibujada en CSS cuando el producto aún no tiene captura ni video. */
const MockScreen = ({ product }) => (
    <div className="sw-mock" aria-hidden="true">
        <div className="sw-mock__side">
            <div className="sw-mock__logo" />
            <div className="sw-mock__nav sw-mock__nav--active" />
            <div className="sw-mock__nav" />
            <div className="sw-mock__nav" />
            <div className="sw-mock__nav" />
            <div className="sw-mock__nav" />
        </div>
        <div className="sw-mock__main">
            <div className="sw-mock__top">
                <span className="sw-mock__title">{product.name}</span>
                <span className="sw-mock__pill" />
            </div>
            <div className="sw-mock__cards">
                <div className="sw-mock__card"><b>128</b><small>hoy</small></div>
                <div className="sw-mock__card"><b>1,240</b><small>este mes</small></div>
                <div className="sw-mock__card"><b>98%</b><small>al día</small></div>
            </div>
            <div className="sw-mock__chart">
                {[35, 55, 40, 70, 60, 85, 65, 90, 75, 95].map((h, i) => (
                    <span className="sw-mock__bar" style={{ height: `${h}%`, animationDelay: `${i * 60}ms` }} key={i} />
                ))}
            </div>
            <div className="sw-mock__rows">
                <div className="sw-mock__row" />
                <div className="sw-mock__row" />
                <div className="sw-mock__row" />
            </div>
        </div>
    </div>
);

const Media = ({ product }) => {
    const { media = {} } = product;
    if (media.video) {
        return (
            <video autoPlay muted loop playsInline preload="metadata" poster={media.poster || undefined} aria-label={`Demostración de ${product.name}`}>
                <source src={media.video} type="video/mp4" />
                {media.screenshot && <img src={media.screenshot} alt={`Pantalla de ${product.name}`} />}
            </video>
        );
    }
    if (media.screenshot) {
        return <img src={media.screenshot} alt={`Pantalla de ${product.name}`} loading="lazy" />;
    }
    return <MockScreen product={product} />;
};

/**
 * "Nuestros softwares": pestañas por producto + mockup de laptop con captura/video/UI de ejemplo
 * + beneficios y CTA. Rota automáticamente cada 7 s hasta que el usuario interactúa o pasa el ratón.
 * Datos: src/data/products.mjs.
 */
const SoftwareShowcase = ({ products = PRODUCTS, interval = 7000 }) => {
    const [index, setIndex] = useState(0);
    const [paused, setPaused] = useState(false);
    const [touched, setTouched] = useState(false);
    const reduced = useRef(false);

    useEffect(() => {
        if (typeof window !== 'undefined' && window.matchMedia) {
            reduced.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        }
    }, []);

    useEffect(() => {
        if (products.length < 2 || paused || touched || reduced.current) return undefined;
        const id = setInterval(() => setIndex((i) => (i + 1) % products.length), interval);
        return () => clearInterval(id);
    }, [products.length, paused, touched, interval]);

    if (!products.length) return null;
    const p = products[Math.min(index, products.length - 1)];
    const select = (i) => {
        setTouched(true);
        setIndex(i);
    };
    const locale = p.currency === 'DOP' ? 'es-DO' : 'en-US';
    const price = p.pricing?.public && p.pricing.from != null ? formatPrice(p.pricing.from, p.currency, locale) : null;
    const siteLink = p.links?.site;

    return (
        <section
            className="sw-showcase"
            id="softwares"
            aria-labelledby="sw-title"
            style={{ '--sw-accent': p.accent || '#E54D42' }}
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
        >
            <div className="container">
                <div className="text-center" data-reveal="up">
                    <span className="sw-showcase__eyebrow">Software propio</span>
                    <h2 id="sw-title" className="sw-showcase__title">Nuestros softwares</h2>
                    <p className="sw-showcase__subtitle">
                        Plataformas desarrolladas y mantenidas por Coralan Multiservice, listas para su empresa y con
                        facturación electrónica e-CF integrada donde aplica.
                    </p>
                </div>

                <ul className="sw-tabs" role="tablist" aria-label="Productos" data-reveal="up" data-reveal-delay="1">
                    {products.map((prod, i) => (
                        <li key={prod.slug} role="presentation" style={{ '--sw-accent': prod.accent || '#E54D42' }}>
                            <button
                                type="button"
                                role="tab"
                                id={`sw-tab-${prod.slug}`}
                                aria-selected={i === index}
                                aria-controls="sw-panel"
                                className="sw-tab"
                                onClick={() => select(i)}
                            >
                                <i className={prod.icon} aria-hidden="true"></i>
                                {prod.name}
                            </button>
                        </li>
                    ))}
                </ul>

                <div id="sw-panel" role="tabpanel" aria-labelledby={`sw-tab-${p.slug}`} className="sw-panel sw-panel--enter" key={p.slug}>
                    <div className="sw-panel__media" data-reveal="left" data-reveal-delay="2">
                        <div className="sw-laptop">
                            <div className="sw-laptop__screen">
                                <Media product={p} />
                            </div>
                            <div className="sw-laptop__base" />
                        </div>
                    </div>
                    <div className="sw-panel__text" data-reveal="right" data-reveal-delay="3">
                        {p.tag && <span className="sw-panel__tag">{p.tag}</span>}
                        <h3 className="sw-panel__name">{p.name}</h3>
                        <p className="sw-panel__tagline">{p.pitch || p.tagline}</p>
                        {p.audience && (
                            <p className="sw-panel__audience">
                                <i className="fas fa-store mr-2" aria-hidden="true"></i>
                                {p.audience}
                            </p>
                        )}
                        {p.problems?.length > 0 && (
                            <>
                                <p className="sw-panel__label">¿Qué resuelve?</p>
                                <ul className="sw-panel__list sw-panel__list--problems">
                                    {p.problems.slice(0, 3).map((h) => (
                                        <li key={h}>{h}</li>
                                    ))}
                                </ul>
                            </>
                        )}
                        {p.highlights?.length > 0 && (
                            <>
                                <p className="sw-panel__label">Cómo lo hace</p>
                                <ul className="sw-panel__list">
                                    {p.highlights.slice(0, 3).map((h) => (
                                        <li key={h}>{h}</li>
                                    ))}
                                </ul>
                            </>
                        )}
                        <p className="sw-panel__price">
                            {price ? (
                                <>
                                    Desde <strong>{price}</strong> / {p.pricing.period || 'mes'}
                                </>
                            ) : (
                                'Precio según plan — solicite una demostración.'
                            )}
                        </p>
                        <div className="sw-panel__actions">
                            <a href="#contactenos" className="sw-btn sw-btn--primary">Solicitar demo</a>
                            {siteLink &&
                                (isInternal(siteLink) ? (
                                    <Link to={siteLink} className="sw-btn sw-btn--ghost">Ver más</Link>
                                ) : (
                                    <a href={siteLink} target="_blank" rel="noopener noreferrer" className="sw-btn sw-btn--ghost">
                                        Visitar {p.name} <i className="fas fa-external-link-alt ml-1" aria-hidden="true"></i>
                                    </a>
                                ))}
                            <Link to="/productos" className="sw-btn sw-btn--ghost">Todos los productos</Link>
                        </div>
                    </div>
                </div>

                {products.length > 1 && (
                    <div className="sw-dots" role="tablist" aria-label="Cambiar producto">
                        {products.map((prod, i) => (
                            <button
                                key={prod.slug}
                                type="button"
                                className="sw-dot"
                                aria-label={prod.name}
                                aria-current={i === index ? 'true' : undefined}
                                onClick={() => select(i)}
                            />
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
};

export default SoftwareShowcase;
